// import { Dashboard } from './routes/dashboard';
// import { RoleBasedDashboard } from './routes/dashboard/RoleBasedDashboard';
import { BozzettiPage } from './routes/bozzetti';
import React from 'react';
import { createRootRoute, createRoute, createRouter, Outlet, RouterProvider } from '@tanstack/react-router';
import { Dashboard } from './routes/dashboard';
import { CampioniPage } from './routes/campioni';
import { PreCampionaturaPage } from './routes/pre-campionatura';
import { ProduzionePage } from './routes/produzione';
import { GammaPage } from './routes/gamma';
import { UserRole } from './types/user';
import { Login } from './routes/auth/login';
import { LoginCallback } from './routes/auth/login-callback';
import AppLayout from './components/layout/AppLayout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { RequiredAuth } from './components/auth/requiredAuth';
import { Unauthorized } from './routes/auth/unauthorized';
import { CacheDashboard } from './routes/admin/cache-dashboard';
import { UsersPage } from './routes/users/UsersPage';
import { ProductsPage } from './routes/products/ProductsPage';
import { ProductDetailPage } from './routes/products/ProductDetailPage';
import { RequestsList } from './routes/requests/RequestsList';
import { RequestDetail } from './routes/requests/RequestDetail';
import { StatisticsPage } from './routes/statistics/StatisticsPage';
import { AnalyticsDashboard } from './components/analytics/AnalyticsDashboard';
import { KanbanPage } from './routes/kanban';
import ChecklistTestPage from './pages/ChecklistTestPage';
import ChecklistStatsDashboard from './components/dashboard/ChecklistStatsDashboard';
import { StoricoPage } from './routes/storico/StoricoPage';
import { StoricoAdminPage } from './routes/storico/StoricoAdminPage';
import { RequestsPage } from './routes/requests/RequestsPage';
import { TasksPage } from './routes/tasks/TasksPage';
import { ChecklistPage } from './routes/checklist/ChecklistPage';
import { ProfilePage } from './routes/profile/ProfilePage';
import { SettingsPage } from './routes/settings/SettingsPage';
import type { KanbanBoardFilters, KanbanDateField } from './types/api';

// Route radice
const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

// Route pubblica (senza protezione)
const publicRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'public',
  component: () => <Outlet />,
});

// Route protetta (richiede autenticazione)
const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  component: () => (
    <ProtectedRoute requireAnyRole>
      <AppLayout>
        <Outlet />
      </AppLayout>
    </ProtectedRoute>
  ),
});

// Route admin (richiede autenticazione + ruolo admin)
const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  component: () => (
    <RequiredAuth requiredRoles={[UserRole.Admin]}>
      <AppLayout>
        <Outlet />
      </AppLayout>
    </RequiredAuth>
  ),
});

const cacheDashboardRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/cache-dashboard',
  component: CacheDashboard,
});

const usersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/utenti',
  component: UsersPage,
});

// Routes pubbliche
const loginRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: '/login',
  component: Login,
});

const loginCallbackRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: '/login/callback',
  component: LoginCallback,
});

const unauthorizedRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: '/unauthorized',
  component: Unauthorized,
});

// Routes protette normali
const dashboardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/dashboard',
  component: Dashboard,
});

const indexRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/',
  component:  Dashboard,
});

const productsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/products',
  component: ProductsPage,
});

const storicoRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/storico',
  component: StoricoPage,
});

const requestsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/requests',
  component: RequestsPage,
});

const requestDetailRoute2 = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/requests/$requestId',
  component: RequestDetail,
  validateSearch: (search: Record<string, unknown>) => ({
    from: (search.from as string) || undefined,
  }),
});

const requestEditRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/requests/$requestId/edit',
  component: RequestDetail, // Puoi usare lo stesso componente con un parametro edit
  validateSearch: (search: Record<string, unknown>) => ({
    from: (search.from as string) || undefined,
  }),
});

const requestNewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/requests/new',
  component: RequestDetail, // Riusa il componente per creare nuova richiesta
});

const tasksRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/tasks',
  component: TasksPage,
});

const checklistRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/checklist',
  component: ChecklistPage,
});

const storicoAdminRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/storico-admin',
  component: StoricoAdminPage,
});

// Route per aggiungere un nuovo prodotto (richiede permesso specifico)
const newProductRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/products/new',
  component: () => (
    <RequiredAuth requiredPermissions={['add-product']}>
      <ProductDetailPage />
    </RequiredAuth>
  ),
});

// Route per la sezione richieste vecchia (lista)
const requestsListRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/requests-list',
  component: RequestsList,
});

const requestDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/requests-list/$requestId',
  component: RequestDetail,
});

// Route per modificare un prodotto esistente (richiede permesso specifico)
const editProductRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/products/$id',
  component: () => (
    <RequiredAuth requiredPermissions={['edit-product']}>
      <ProductDetailPage />
    </RequiredAuth>
  ),
});


// Schema per i search params della board Kanban (/board): filtri persona/richiesta/periodo,
// condiviso con use-board-filters.ts. `requestId` tollera sia number che string in ingresso
// (il link da RequestTasksSection.tsx passa una stringa); `assigneeIds` tollera un valore
// singolo o un array. Valori non validi vengono scartati silenziosamente (undefined).
const boardSearchSchema = (search: Record<string, unknown>): KanbanBoardFilters => {
  const requestIdNum = Number(search.requestId);
  const requestId =
    search.requestId != null && !Number.isNaN(requestIdNum) && requestIdNum > 0 ? requestIdNum : undefined;

  const rawAssignees = Array.isArray(search.assigneeIds)
    ? search.assigneeIds
    : search.assigneeIds != null
      ? [search.assigneeIds]
      : [];
  const assigneeIds = rawAssignees
    .map((v) => Number(v))
    .filter((n) => Number.isFinite(n) && n > 0);

  const validDateFields: KanbanDateField[] = ['planned', 'created', 'completed'];
  const dateField = validDateFields.includes(search.dateField as KanbanDateField)
    ? (search.dateField as KanbanDateField)
    : undefined;

  const dateRe = /^\d{4}-\d{2}-\d{2}$/;
  const from = typeof search.from === 'string' && dateRe.test(search.from) ? search.from : undefined;
  const to = typeof search.to === 'string' && dateRe.test(search.to) ? search.to : undefined;

  return {
    requestId,
    assigneeIds: assigneeIds.length ? assigneeIds : undefined,
    dateField,
    from,
    to,
  };
};

const boardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/board',
  component: KanbanPage,
  validateSearch: boardSearchSchema,
});

const statisticsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/statistics',
  component: StatisticsPage,
});

// Schema condiviso per i search params delle pagine tipo (drill-down e shareable URLs)
const requestTypeSearchSchema = (search: Record<string, unknown>) => ({
  q: (search.q as string) || undefined,
  year: search.year ? Number(search.year) : undefined,
  status: search.status ? Number(search.status) : undefined,
  startDate: (search.startDate as string) || undefined,
  endDate: (search.endDate as string) || undefined,
  pageNumber: search.pageNumber ? Number(search.pageNumber) : undefined,
  pageSize: search.pageSize ? Number(search.pageSize) : undefined,
});

// Routes per gestione richieste role-based
const bozzettiRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/bozzetti',
  component: BozzettiPage,
  validateSearch: requestTypeSearchSchema,
});

const campioniRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/campioni',
  component: CampioniPage,
  validateSearch: requestTypeSearchSchema,
});

const preCampionaturaRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/pre-campionatura',
  component: PreCampionaturaPage,
  validateSearch: requestTypeSearchSchema,
});

const produzioneRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/produzione',
  component: ProduzionePage,
  validateSearch: requestTypeSearchSchema,
});

const gammaRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/gamma',
  component: GammaPage,
  validateSearch: requestTypeSearchSchema,
});

const analyticsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/analytics',
  component: AnalyticsDashboard,
});

const checklistTestRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/test/checklist',
  component: ChecklistTestPage,
});

const checklistStatsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/checklist/stats',
  component: ChecklistStatsDashboard,
});

// Route Profilo utente
const profileRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/profile',
  component: ProfilePage,
});

// Route Impostazioni utente
const settingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/settings',
  component: SettingsPage,
});

// Costruisci l'albero delle route
const routeTree = rootRoute.addChildren([
  publicRoute.addChildren([
    loginRoute,
    loginCallbackRoute,
    unauthorizedRoute,
  ]),
  protectedRoute.addChildren([
    indexRoute,
    dashboardRoute,
    productsRoute,
    storicoRoute,
    requestsRoute,
    requestDetailRoute2,
    requestEditRoute,
    requestNewRoute,
    tasksRoute,
    checklistRoute,
    newProductRoute,
    editProductRoute,
    requestsListRoute,
    requestDetailRoute,
    boardRoute,
    statisticsRoute,
    bozzettiRoute,
    campioniRoute,
    preCampionaturaRoute,
    produzioneRoute,
    gammaRoute,
    analyticsRoute,
    checklistTestRoute,
    checklistStatsRoute,
    profileRoute,
    settingsRoute
  ]),
  adminRoute.addChildren([
    cacheDashboardRoute,
    usersRoute,
    storicoAdminRoute
  ]),
]);

// Crea il router
export const routerInstance = createRouter({ routeTree });

export function Router() {
  return (
    <RouterProvider router={routerInstance} />
  );
}